import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  useGetListOfFiltersQuery,
  useGetFilterByIdQuery,
  useCreateFilterMutation,
  useUpdateFilterMutation,
  useDeleteFilterMutation,
} from "@/features/bughurd/api"; // Adjust import path as needed

interface Filter {
  id: string;
  type:
    | "status"
    | "priority"
    | "due_date"
    | "start_date"
    | "updated_date"
    | "project"
    | "tags"
    | "assignee"
    | "created_by";
  value: string | string[];
  startDate?: string;
  endDate?: string;
}

interface SavedFilter {
  id: string;
  name: string;
  filters: Filter[];
  createdAt: string;
  updatedAt: string;
  project?: {
    _id: string;
    name: string;
    code: string;
  };
  createdBy?: {
    _id: string;
    userId: string;
    email: string;
    fullName: string;
  };
}

export const useFilterStorage = (viewType: string, isMyTaskView: boolean) => {
  const { id: projectId } = useParams<{ id: string }>();
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);

  // Determine project parameter
  const getProjectParam = () => {
    if (isMyTaskView) {
      return undefined; // Don't send project for my tasks view
    }
    return projectId;
  };

  // RTK Query hooks
  const {
    data: filtersResponse,
    isLoading,
    refetch,
    error,
  } = useGetListOfFiltersQuery(
    {
      page: 1,
      size: 100, // Adjust as needed
      project: getProjectParam(),
    },
    {
      skip: false, // Always fetch
    }
  );
  const [createFilterMutation, { isLoading: isCreating }] =
    useCreateFilterMutation();
  const [updateFilterMutation, { isLoading: isUpdating }] =
    useUpdateFilterMutation();
  const [deleteFilterMutation, { isLoading: isDeleting }] =
    useDeleteFilterMutation();

  // Helper function to transform API filter data
  const transformApiFilter = (apiFilter: any): Filter => {
    return {
      id: apiFilter.id,
      type: apiFilter.type,
      value: apiFilter.value,
      startDate: apiFilter.startDate,
      endDate: apiFilter.endDate,
    };
  };

  // Helper function to transform filter for API
  const transformFilterForApi = (filter: Filter): any => {
    return {
      id: filter.id,
      type: filter.type,
      value: filter.value,
      startDate: filter.startDate,
      endDate: filter.endDate,
    };
  };

  // Update local state when API data changes
  useEffect(() => {
    if (filtersResponse?.data?.decryptedData?.items) {
      // Transform API data to match our SavedFilter interface
      const transformedFilters: SavedFilter[] =
        filtersResponse.data.decryptedData.items.map((apiFilter: any) => ({
          id: apiFilter._id,
          name: apiFilter.name,
          filters: (apiFilter.filters || []).map(transformApiFilter), // Transform each filter
          createdAt: apiFilter.createdAt,
          updatedAt: apiFilter.updatedAt,
          project: apiFilter.project, // Keep full project object
          createdBy: apiFilter.createdBy, // Keep full createdBy object
        }));

      setSavedFilters(
        transformedFilters.sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )
      );
    } else if (
      filtersResponse?.data?.decryptedData?.items === undefined &&
      !isLoading
    ) {
      // Handle case when API returns empty or no items
      setSavedFilters([]);
    }
  }, [filtersResponse, isLoading]);

  // Load filters function (mainly for manual refresh)
  const loadFilters = async (): Promise<void> => {
    try {
      await refetch();
    } catch (error) {
      console.error("Error loading filters:", error);
      throw error; // Re-throw to handle in component
    }
  };

  // Save or update a filter
  const saveFilter = async (
    filter: SavedFilter
  ): Promise<{ isNew: boolean; response?: any }> => {
    try {
      const payload = {
        name: filter.name,
        filters: filter.filters.map(transformFilterForApi), // Transform filters for API
        viewType,
        ...(!isMyTaskView && projectId && { project: projectId }),
      };

      if (filter.id && filter.id.startsWith("filter-")) {
        // This is a new filter (temporary ID)
        const response = await createFilterMutation(payload).unwrap();
        return { isNew: true, response };
      } else if (filter.id && !filter.id.startsWith("filter-")) {
        // This is an existing filter with real ID
        const response = await updateFilterMutation({
          filterId: filter.id,
          ...payload,
        }).unwrap();
        return { isNew: false, response };
      } else {
        // New filter without ID
        const response = await createFilterMutation(payload).unwrap();
        return { isNew: true, response };
      }
    } catch (error: any) {
      console.error("Error saving filter:", error);
      let errorMessage = "Failed to save filter";

      // Handle different error response structures
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.error) {
        errorMessage = error.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  };

  // Delete a filter
  const deleteFilter = async (filterId: string): Promise<void> => {
    try {
      await deleteFilterMutation(filterId).unwrap();
    } catch (error: any) {
      console.error("Error deleting filter:", error);
      let errorMessage = "Failed to delete filter";

      // Handle different error response structures
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.error) {
        errorMessage = error.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  };

  // Get filter by ID
  const getFilterById = (filterId: string): SavedFilter | null => {
    return savedFilters.find((f) => f.id === filterId) || null;
  };

  // Duplicate a filter
  const duplicateFilter = async (
    filterId: string,
    newName: string
  ): Promise<void> => {
    try {
      const originalFilter = getFilterById(filterId);
      if (!originalFilter) {
        throw new Error("Filter not found");
      }

      const payload = {
        name: newName,
        filters: originalFilter.filters.map(transformFilterForApi), // Transform filters for API
        viewType,
        ...(!isMyTaskView && projectId && { project: projectId }),
      };

      await createFilterMutation(payload).unwrap();
    } catch (error: any) {
      console.error("Error duplicating filter:", error);
      let errorMessage = "Failed to duplicate filter";

      // Handle different error response structures
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.error) {
        errorMessage = error.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  };

  // Get total count of filters
  const getTotalCount = (): number => {
    return filtersResponse?.data?.decryptedData?.total || 0;
  };

  // Check if any operation is in progress
  const isOperationInProgress = isCreating || isUpdating || isDeleting;

  // Helper function to validate filter structure
  const validateFilter = (filter: Filter): boolean => {
    const validTypes = [
      "status",
      "priority",
      "due_date",
      "start_date",
      "updated_date",
      "project",
      "recurring",
      "tags",
      "assignee",
      "created_by",
    ];

    // Check if type is valid
    if (!validTypes.includes(filter.type)) {
      return false;
    }

    // Check if value is appropriate for the type
    if (filter.type === "recurring") {
      return typeof filter.value === "boolean";
    } else if (
      ["status", "priority", "project", "tags", "assignee", "created_by"].includes(
        filter.type
      )
    ) {
      return Array.isArray(filter.value);
    } else {
      return typeof filter.value === "string";
    }
  };

  // Helper function to sanitize filters before saving
  const sanitizeFilters = (filters: Filter[]): Filter[] => {
    return filters.filter(validateFilter).map((filter) => ({
      ...filter,
      // Ensure recurring filters have proper boolean values
      value: filter.type === "recurring" ? Boolean(filter.value) : filter.value,
    }));
  };

  return {
    savedFilters,
    isLoading: isLoading || isOperationInProgress,
    saveFilter,
    deleteFilter,
    getFilterById,
    duplicateFilter,
    loadFilters,
    getTotalCount,
    error,
    validateFilter,
    sanitizeFilters,
  };
};
